import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { finalize, map } from 'rxjs';
import { Rentabilidade } from '../../../../core/models/parametros/beneficios.model';
import { BeneficiosService } from '../../../../core/services/parametros/beneficios.service';
import { ToastService } from '../../../../core/services/toast.service';
import { descreverRentabilidade } from '../../../../core/utils/parametros/beneficios.defaults';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { LoaderService } from '../../../../shared/services/loader/loader.service';

/** De 0 a 100, com até duas casas decimais (rentabilidade mínima zero é aceita). */
const percentualOk = (valor: number | null) =>
  valor !== null && valor >= 0 && valor <= 100 && /^\d+(\.\d{1,2})?$/.test(String(valor));

/** Opcional: em branco ou os dois percentuais preenchidos. */
function erroFaixa({ percentualMinimo, percentualMaximo }: Rentabilidade): string {
  if (percentualMinimo === null && percentualMaximo === null) {
    return '';
  }
  if (!percentualOk(percentualMinimo) || !percentualOk(percentualMaximo)) {
    return 'Informe os dois percentuais de 0 a 100, com até duas casas decimais.';
  }
  return percentualMinimo! > percentualMaximo! ? 'O percentual mínimo não pode ser maior que o máximo.' : '';
}

/** Opcional. Só confere se está dentro da faixa quando a faixa foi preenchida e é válida. */
function erroPadrao({ percentualMinimo, percentualMaximo, percentualPadrao }: Rentabilidade, faixaValida: boolean): string {
  if (percentualPadrao === null) {
    return '';
  }
  if (!percentualOk(percentualPadrao)) {
    return 'Informe o valor padrão de 0 a 100, com até duas casas decimais.';
  }
  const temFaixa = faixaValida && percentualMinimo !== null && percentualMaximo !== null;
  return temFaixa && (percentualPadrao < percentualMinimo! || percentualPadrao > percentualMaximo!)
    ? 'O valor padrão deve estar dentro da faixa de rentabilidade.'
    : '';
}

/** Seção "Rentabilidade": a faixa e o valor padrão de rentabilidade do plano. */
@Component({
  selector: 'app-beneficios-rentabilidade',
  standalone: true,
  imports: [ReactiveFormsModule, VivestModalAreaVisivelDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './beneficios-rentabilidade.component.html',
  styleUrl: './beneficios-rentabilidade.component.scss',
})
export class BeneficiosRentabilidadeComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly beneficiosService = inject(BeneficiosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly planoId = input.required<number>();
  readonly nomePlano = input('');

  /** Último estado salvo, para o Cancelar e para saber se há alterações. */
  private readonly carregado = signal<Rentabilidade | null>(null);
  readonly carregou = computed(() => this.carregado() !== null);

  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  readonly tentouSalvar = signal(false);

  readonly form = this.fb.group({
    percentualMinimo: this.fb.control<number | null>(null),
    percentualMaximo: this.fb.control<number | null>(null),
    percentualPadrao: this.fb.control<number | null>(null),
  });

  readonly valor = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });

  readonly temAlteracoes = computed(() => {
    const carregado = this.carregado();
    return !!carregado && JSON.stringify(this.valor()) !== JSON.stringify(carregado) && !this.salvando();
  });

  private readonly erroFaixaAtual = computed(() => erroFaixa(this.valor()));
  private readonly erroPadraoAtual = computed(() => erroPadrao(this.valor(), !this.erroFaixaAtual()));
  readonly invalido = computed(() => !!this.erroFaixaAtual() || !!this.erroPadraoAtual());
  readonly erro = computed(() => (this.tentouSalvar() ? this.erroFaixaAtual() : ''));
  readonly erroPadrao = computed(() => (this.tentouSalvar() ? this.erroPadraoAtual() : ''));
  readonly resumo = computed(() => descreverRentabilidade(this.valor()));

  ngOnInit(): void {
    this.loaderService.showLoader();
    this.beneficiosService
      .obterRentabilidade(this.planoId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rentabilidade) => {
          this.aplicar(rentabilidade);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a rentabilidade.', 'error');
        },
      });
  }

  salvar(): void {
    this.tentouSalvar.set(true);
    if (!this.temAlteracoes()) {
      return;
    }
    if (this.invalido()) {
      this.toastService.show('Revise a rentabilidade antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.invalido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .salvarRentabilidade(this.planoId(), this.form.getRawValue())
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salva) => {
          this.aplicar(salva);
          this.toastService.show('Rentabilidade do plano salva.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar a rentabilidade.', 'error'),
      });
  }

  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  private aplicar(rentabilidade: Rentabilidade): void {
    this.form.reset(structuredClone(rentabilidade));
    this.carregado.set(structuredClone(rentabilidade));
    this.tentouSalvar.set(false);
  }
}
